import {
  GetPendingPunchesQuery,
  RoleType,
  useGetPendingPunchesQuery,
  useGetUsersQuery,
  useGetUsersWithRewardsQuery,
  UsersPermissionsUser,
  useUpdatePendingPunchMutation,
  useUpdateUserRewardCoinsMutation
} from "@mc-intranet/shared";
import { Cancel, CheckCircle, FilterList } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ListAltIcon from "@mui/icons-material/ListAlt";
import RemoveIcon from "@mui/icons-material/Remove";
import StarsIcon from "@mui/icons-material/Stars";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAtomValue } from "jotai";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { authStorageAtom } from "../../atoms";
import { useGraphQLClient } from "../../cache";
import { getNumericUserId } from "../../services";

type PendingPunchType = NonNullable<
  GetPendingPunchesQuery["pendingPunches"][0]
>;

type OrderBy = "user" | "date" | "createdAt" | "rewardCoins";
type Order = "asc" | "desc";
type ViewMode = "approvazione" | "punteggi";

// Componente separato per la riga della tabella punteggi (con proprio stato locale)
interface RewardRowProps {
  user: UsersPermissionsUser;
  hadRequestsPrevMonth: boolean;
  onSave: (userId: string, coins: number) => void;
}

const RewardRow: React.FC<RewardRowProps> = ({
  user,
  hadRequestsPrevMonth,
  onSave
}) => {
  const initialCoins = user?.rewardCoins ?? 0;
  const [localCoins, setLocalCoins] = useState<number>(initialCoins);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sincronizza se cambia dall'esterno (es. dopo un refetch)
  useEffect(() => {
    setLocalCoins(user?.rewardCoins ?? 0);
  }, [user?.rewardCoins]);

  const triggerSave = useCallback(
    (value: number) => {
      // Cancella il timeout precedente: la mutation parte solo dopo 800ms
      // dall'ultimo click, evitando una chiamata per ogni pressione di +/-
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSave(user.documentId as string, value);
      }, 800);
    },
    [onSave, user.documentId]
  );

  const handleIncrement = () => {
    const next = localCoins + 5;
    setLocalCoins(next);
    triggerSave(next);
  };

  const handleDecrement = () => {
    const next = localCoins - 5;
    setLocalCoins(next);
    triggerSave(next);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    const next = isNaN(parsed) ? 0 : parsed;
    setLocalCoins(next);
    triggerSave(next);
  };

  const isDirty = localCoins !== initialCoins;

  return (
    <TableRow>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            component="span"
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: user?.color || "#ccc",
              flexShrink: 0
            }}
          />
          <Typography variant="body2">
            {user?.firstName} {user?.lastName}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={0.5}>
          <StarsIcon
            sx={{
              color: (user?.rewardCoins ?? 0) <= 0 ? "#999" : "#FFD700",
              fontSize: 18
            }}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: (user?.rewardCoins ?? 0) <= 0 ? "error.main" : "#FFD700"
            }}
          >
            {user?.rewardCoins ?? 0}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        {hadRequestsPrevMonth ? (
          <Tooltip title="Ha fatto richieste il mese scorso">
            <Typography variant="body2" color="warning.main" fontWeight={600}>
              ✗ Richieste effettuate
            </Typography>
          </Tooltip>
        ) : (
          <Tooltip title="Nessuna richiesta il mese scorso – merita il bonus punti">
            <Typography variant="body2" color="success.main" fontWeight={600}>
              ✓ Nessuna richiesta
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={handleDecrement} color="error">
            <RemoveIcon fontSize="small" />
          </IconButton>
          <TextField
            size="small"
            value={localCoins}
            onChange={handleInputChange}
            inputProps={{
              style: {
                textAlign: "center",
                width: 60,
                fontWeight: isDirty ? 700 : 400
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderColor: isDirty ? "warning.main" : undefined
              }
            }}
          />
          <IconButton size="small" onClick={handleIncrement} color="success">
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const ApprovazionePage = () => {
  const authStorage = useAtomValue(authStorageAtom);
  const { user, role } = authStorage;
  const { client, token } = useGraphQLClient();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<OrderBy>("date");
  const [order, setOrder] = useState<Order>("desc");
  const [selectedUser, setSelectedUser] = useState<UsersPermissionsUser | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Moment | null>(moment());
  const [showProcessed, setShowProcessed] = useState(false);
  const [selectedPunches, setSelectedPunches] = useState<Set<string>>(
    new Set()
  );
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "approve" | "reject" | null;
  }>({
    open: false,
    type: null
  });
  const [viewMode, setViewMode] = useState<ViewMode>("approvazione");
  const [showOnlyNoPrevRequests, setShowOnlyNoPrevRequests] = useState(true);

  const canSeeOtherUsers = role === RoleType.account || role === RoleType.admin;

  const { data: usersData } = useGetUsersQuery(
    client,
    {},
    {
      enabled: !!user && canSeeOtherUsers,
      onError: (error: any) => {
        toast.error(error?.response?.error.message, {
          position: toast.POSITION.TOP_CENTER
        });
      }
    }
  );

  const { data: usersWithRewardsData, refetch: refetchUsersWithRewards } =
    useGetUsersWithRewardsQuery(
      client,
      {},
      {
        enabled: !!user && canSeeOtherUsers,
        onError: (error: any) => {
          toast.error(error?.response?.error.message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );

  // Query per le richieste del mese precedente (per determinare il bonus)
  const prevMonthStart = moment()
    .subtract(1, "month")
    .startOf("month")
    .format("YYYY-MM-DD");
  const prevMonthEnd = moment()
    .subtract(1, "month")
    .endOf("month")
    .format("YYYY-MM-DD");

  const { data: prevMonthData } = useGetPendingPunchesQuery(
    client,
    { startDate: prevMonthStart, endDate: prevMonthEnd },
    {
      enabled: !!user && canSeeOtherUsers && viewMode === "punteggi"
    }
  );

  // Set di userId che hanno avuto richieste **approvate** il mese scorso
  const usersWithPrevMonthRequests = React.useMemo(() => {
    const ids = new Set<string>();
    prevMonthData?.pendingPunches?.forEach((p) => {
      if (p?.approved !== true) return; // considera solo le approvate
      const uid = p?.day?.user?.documentId;
      if (uid) ids.add(uid);
    });
    return ids;
  }, [prevMonthData]);

  const { mutate: updateUserRewardCoins } = useUpdateUserRewardCoinsMutation(
    client,
    {
      onSuccess: () => {
        refetchUsersWithRewards();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.error?.message ||
            "Errore nell'aggiornamento dei punti",
          { position: toast.POSITION.TOP_CENTER }
        );
      }
    }
  );

  const handleSaveRewardCoins = useCallback(
    async (userDocumentId: string, rewardCoins: number) => {
      try {
        // updateUsersPermissionsUser vuole ancora l'id numerico, non il documentId
        const userId = await getNumericUserId(userDocumentId, token);
        updateUserRewardCoins({ userId, rewardCoins });
      } catch (error) {
        toast.error("Errore nell'aggiornamento dei punti", {
          position: toast.POSITION.TOP_CENTER
        });
      }
    },
    [updateUserRewardCoins, token]
  );

  const { data, isLoading, refetch } = useGetPendingPunchesQuery(
    client,
    {
      startDate: selectedDate?.startOf("month").format("YYYY-MM-DD"),
      endDate: selectedDate?.endOf("month").format("YYYY-MM-DD")
    },
    {
      enabled: !!user && !!selectedDate,
      onError: (error: any) => {
        toast.error(
          error?.response?.error.message || "Errore nel caricamento",
          {
            position: toast.POSITION.TOP_CENTER
          }
        );
      }
    }
  );

  const { mutate: updatePendingPunch } = useUpdatePendingPunchMutation(client, {
    onSuccess: (data) => {
      const approved = data?.updatePendingPunch?.approved;

      if (approved === true) {
        toast.success("Modifica approvata con successo", {
          position: toast.POSITION.TOP_CENTER
        });
      } else {
        toast.success("Modifica rifiutata con successo", {
          position: toast.POSITION.TOP_CENTER
        });
      }
      refetch();
      setProcessingId(null);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.error.message || "Errore nell'aggiornamento",
        {
          position: toast.POSITION.TOP_CENTER
        }
      );
      setProcessingId(null);
    }
  });

  const handleApprove = (pendingPunchId: string) => {
    // Trova la pending punch per controllare il punteggio dell'utente
    const pendingPunch = pendingPunches.find(
      (p) => p.documentId === pendingPunchId
    );
    const userRewardCoins = pendingPunch?.day?.user?.rewardCoins;

    if (userRewardCoins === undefined || userRewardCoins === null) {
      toast.error("Impossibile verificare il punteggio dell'utente", {
        position: toast.POSITION.TOP_CENTER
      });
      return;
    }

    if (userRewardCoins <= 0) {
      toast.error(
        "L'utente non ha punti sufficienti per approvare questa richiesta",
        {
          position: toast.POSITION.TOP_CENTER
        }
      );
      return;
    }

    setProcessingId(pendingPunchId);
    updatePendingPunch({
      punchId: pendingPunchId,
      userId: user?.documentId as string,
      approved: true
    });
  };

  const handleReject = (pendingPunchId: string) => {
    setProcessingId(pendingPunchId);
    updatePendingPunch({
      punchId: pendingPunchId,
      userId: user?.documentId as string,
      approved: false
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      type: null
    });
  };

  const handleConfirm = () => {
    // Gestione solo delle azioni batch
    handleCloseConfirmDialog();
    if (confirmDialog.type === "approve") {
      processBatch(true);
    } else {
      processBatch(false);
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleUserChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: UsersPermissionsUser | null
  ) => {
    setSelectedUser(value);
  };

  const handleDateChange = (newDate: Moment | null) => {
    setSelectedDate(newDate);
  };

  // Gestione selezione multipla
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectablePunches = filteredAndSortedPunches
        .filter((p) => p?.approved === null)
        .map((p) => p.documentId as string);
      setSelectedPunches(new Set(selectablePunches));
    } else {
      setSelectedPunches(new Set());
    }
  };

  const handleSelectPunch = (punchId: string) => {
    const newSelected = new Set(selectedPunches);
    if (newSelected.has(punchId)) {
      newSelected.delete(punchId);
    } else {
      newSelected.add(punchId);
    }
    setSelectedPunches(newSelected);
  };

  const handleBatchApprove = () => {
    setConfirmDialog({
      open: true,
      type: "approve"
    });
  };

  const handleBatchReject = () => {
    setConfirmDialog({
      open: true,
      type: "reject"
    });
  };

  const processBatch = async (approved: boolean) => {
    let punchIds = Array.from(selectedPunches);

    // Se stiamo approvando, filtra le richieste degli utenti con punteggio insufficiente
    if (approved) {
      const validPunchIds: string[] = [];
      const invalidPunchIds: string[] = [];

      punchIds.forEach((punchId) => {
        const pendingPunch = pendingPunches.find(
          (p) => p.documentId === punchId
        );
        const userRewardCoins = pendingPunch?.day?.user?.rewardCoins;

        if (
          userRewardCoins !== undefined &&
          userRewardCoins !== null &&
          userRewardCoins > 0
        ) {
          validPunchIds.push(punchId);
        } else {
          invalidPunchIds.push(punchId);
        }
      });

      if (invalidPunchIds.length > 0) {
        toast.warning(
          `${invalidPunchIds.length} ${
            invalidPunchIds.length === 1 ? "richiesta" : "richieste"
          } ${
            invalidPunchIds.length === 1 ? "saltata" : "saltate"
          } per punteggio insufficiente`,
          {
            position: toast.POSITION.TOP_CENTER
          }
        );
      }

      punchIds = validPunchIds;

      if (punchIds.length === 0) {
        toast.error(
          "Nessuna richiesta può essere approvata: tutti gli utenti hanno punteggio insufficiente",
          {
            position: toast.POSITION.TOP_CENTER
          }
        );
        setSelectedPunches(new Set());
        return;
      }
    }

    const CHUNK_SIZE = 10; // Processa 10 richieste alla volta
    const chunks = [];

    // Dividi in chunk
    for (let i = 0; i < punchIds.length; i += CHUNK_SIZE) {
      chunks.push(punchIds.slice(i, i + CHUNK_SIZE));
    }

    let successCount = 0;
    let errorCount = 0;

    try {
      // Processa un chunk alla volta
      for (const chunk of chunks) {
        const promises = chunk.map(
          (punchId) =>
            new Promise((resolve, reject) => {
              updatePendingPunch(
                {
                  punchId,
                  userId: user?.documentId as string,
                  approved
                },
                {
                  onSuccess: () => {
                    successCount++;
                    resolve(true);
                  },
                  onError: (error) => {
                    errorCount++;
                    reject(error);
                  }
                }
              );
            })
        );

        // Attendi che il chunk corrente sia completato prima di passare al prossimo
        await Promise.allSettled(promises);
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} ${successCount === 1 ? "richiesta" : "richieste"} ${
            approved ? "approvata" : "rifiutata"
          } con successo${errorCount > 0 ? `. ${errorCount} errori.` : ""}`,
          {
            position: toast.POSITION.TOP_CENTER
          }
        );
      }

      if (errorCount === punchIds.length) {
        toast.error("Errore nell'elaborazione di tutte le richieste", {
          position: toast.POSITION.TOP_CENTER
        });
      }

      setSelectedPunches(new Set());
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.error.message || "Errore nell'elaborazione batch",
        {
          position: toast.POSITION.TOP_CENTER
        }
      );
    }
  };

  const pendingPunches = React.useMemo(() => {
    return (data?.pendingPunches || []).filter(
      (p): p is PendingPunchType => !!p
    );
  }, [data?.pendingPunches]);

  // Calcola gli utenti che hanno richieste pending
  const usersWithPendingRequests = React.useMemo(() => {
    if (!usersData?.usersPermissionsUsers) return [];

    const userIds = new Set(
      pendingPunches.map((p) => p?.day?.user?.documentId).filter(Boolean)
    );

    return (usersData.usersPermissionsUsers as UsersPermissionsUser[]).filter(
      (user) => userIds.has(user.documentId)
    );
  }, [usersData?.usersPermissionsUsers, pendingPunches]);

  // Filtra e ordina i dati
  const filteredAndSortedPunches = React.useMemo(() => {
    let filtered = [...pendingPunches];

    // Filtro per stato approvazione (toggle)
    if (!showProcessed) {
      // Se showProcessed è false, mostra solo quelle in attesa (approved === null)
      filtered = filtered.filter((pendingPunch) => {
        return pendingPunch?.approved === null;
      });
    }

    // Filtro per utente selezionato
    if (selectedUser) {
      filtered = filtered.filter((pendingPunch) => {
        const dayUser = pendingPunch?.day?.user;
        return dayUser?.documentId === selectedUser.documentId;
      });
    }

    // Ordinamento
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case "user": {
          const aUser = a?.day?.user;
          const bUser = b?.day?.user;
          aValue = `${aUser?.firstName || ""} ${aUser?.lastName || ""}`;
          bValue = `${bUser?.firstName || ""} ${bUser?.lastName || ""}`;
          break;
        }
        case "date":
          aValue = a?.day?.date || "";
          bValue = b?.day?.date || "";
          break;
        case "createdAt":
          aValue = a?.createdAt || "";
          bValue = b?.createdAt || "";
          break;
        case "rewardCoins": {
          const aCoins = a?.day?.user?.rewardCoins ?? -1;
          const bCoins = b?.day?.user?.rewardCoins ?? -1;
          aValue = aCoins;
          bValue = bCoins;
          break;
        }
        default:
          return 0;
      }

      if (order === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [pendingPunches, orderBy, order, selectedUser, showProcessed]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {viewMode === "approvazione" ? (
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 5 }}>
          Approvazione modifiche Timbrature
        </Typography>
      ) : (
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 5 }}>
          Gestione Punteggi Utenti
        </Typography>
      )}

      {/* Barra di ricerca e filtri - sempre visibile */}
      <Card
        elevation={5}
        sx={{
          mb: 3,
          backgroundColor: "background.default",
          borderRadius: 4
        }}
      >
        <CardContent sx={{ "&:last-child": { paddingBottom: "16px" } }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <FilterList color="primary" />
            {canSeeOtherUsers && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => {
                  if (val) setViewMode(val as ViewMode);
                }}
                size="small"
              >
                <ToggleButton value="approvazione">
                  <ListAltIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Approvazione
                </ToggleButton>
                <ToggleButton value="punteggi">
                  <EmojiEventsIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Punteggi
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {canSeeOtherUsers && (
              <Autocomplete
                disablePortal
                id="user-filter-autocomplete"
                sx={{ bgcolor: "background.default", width: 300 }}
                size="small"
                value={selectedUser}
                options={
                  viewMode === "approvazione"
                    ? usersWithPendingRequests
                    : ((usersWithRewardsData?.usersPermissionsUsers ??
                        []) as UsersPermissionsUser[])
                }
                getOptionLabel={(option) =>
                  `${option?.firstName} ${option?.lastName}`
                }
                isOptionEqualToValue={(option, value) =>
                  option.documentId === value.documentId
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filtra per Utente"
                    placeholder="Tutti gli utenti"
                  />
                )}
                onChange={handleUserChange}
              />
            )}
            {viewMode === "approvazione" && (
              <>
                <DatePicker
                  label="Filtra per Mese"
                  slotProps={{ textField: { size: "small" } }}
                  value={selectedDate}
                  onChange={handleDateChange}
                  format="MM/YYYY"
                  views={["month", "year"]}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showProcessed}
                      onChange={(e) => setShowProcessed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Mostra già processate"
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: "auto" }}
                >
                  {filteredAndSortedPunches.length}{" "}
                  {filteredAndSortedPunches.length === 1
                    ? "richiesta"
                    : "richieste"}
                  {selectedUser &&
                    ` per ${selectedUser?.firstName} ${selectedUser?.lastName}`}
                </Typography>
              </>
            )}
            {viewMode === "punteggi" && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyNoPrevRequests}
                    onChange={(e) =>
                      setShowOnlyNoPrevRequests(e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Solo meritevoli"
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Tabella punteggi */}
      {viewMode === "punteggi" && canSeeOtherUsers && (
        <TableContainer
          component={Paper}
          elevation={5}
          sx={{ backgroundColor: "background.default", borderRadius: 4 }}
        >
          <Table size="medium">
            <TableHead
              sx={{
                backgroundColor: "background.paper",
                fontSize: "0.8rem",
                color: "secondary.main"
              }}
            >
              <TableRow>
                <TableCell sx={{ fontSize: "0.8rem", color: "secondary.main" }}>
                  Utente
                </TableCell>
                <TableCell sx={{ fontSize: "0.8rem", color: "secondary.main" }}>
                  Punteggio attuale
                </TableCell>
                <TableCell sx={{ fontSize: "0.8rem", color: "secondary.main" }}>
                  {`Richieste mese scorso (${moment()
                    .subtract(1, "month")
                    .format("MMMM YYYY")})`}
                </TableCell>
                <TableCell sx={{ fontSize: "0.8rem", color: "secondary.main" }}>
                  Modifica punti
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(usersWithRewardsData?.usersPermissionsUsers ?? [])
                .filter((u): u is NonNullable<typeof u> => !!u)
                .filter((u) =>
                  selectedUser ? u.documentId === selectedUser.documentId : true
                )
                .filter((u) =>
                  showOnlyNoPrevRequests
                    ? !usersWithPrevMonthRequests.has(u.documentId as string)
                    : true
                )
                .map((u) => (
                  <RewardRow
                    key={u.documentId}
                    user={u as UsersPermissionsUser}
                    hadRequestsPrevMonth={usersWithPrevMonthRequests.has(
                      u.documentId as string
                    )}
                    onSave={handleSaveRewardCoins}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {viewMode === "approvazione" &&
        (pendingPunches.length === 0 ? (
          <Card
            elevation={5}
            sx={{
              backgroundColor: "background.default",
              borderRadius: 4
            }}
          >
            <CardContent sx={{ "&:last-child": { paddingBottom: "16px" } }}>
              <Typography variant="body1" color="text.secondary" align="center">
                Nessuna modifica in attesa di approvazione
              </Typography>
            </CardContent>
          </Card>
        ) : filteredAndSortedPunches.length === 0 ? (
          <Card
            elevation={5}
            sx={{
              backgroundColor: "background.default",
              borderRadius: 4
            }}
          >
            <CardContent sx={{ "&:last-child": { paddingBottom: "16px" } }}>
              <Typography variant="body1" color="text.secondary" align="center">
                {!showProcessed
                  ? "Nessuna richiesta in attesa per questo mese"
                  : "Nessuna richiesta per questo mese"}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {selectedPunches.size > 0 && (
              <Card
                elevation={5}
                sx={{
                  mb: 2,
                  backgroundColor: "background.default",
                  borderRadius: 4
                }}
              >
                <CardContent sx={{ "&:last-child": { paddingBottom: "16px" } }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body1" fontWeight={600}>
                      {selectedPunches.size}{" "}
                      {selectedPunches.size === 1
                        ? "richiesta selezionata"
                        : "richieste selezionate"}
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={handleBatchApprove}
                    >
                      Approva selezionate
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={handleBatchReject}
                    >
                      Rifiuta selezionate
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}
            <TableContainer
              component={Paper}
              elevation={5}
              sx={{
                backgroundColor: "background.default",
                borderRadius: 4
              }}
            >
              <Table size="medium">
                <TableHead
                  sx={{
                    backgroundColor: "background.paper",
                    fontSize: "0.8rem",
                    color: "secondary.main"
                  }}
                >
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedPunches.size > 0 &&
                          selectedPunches.size <
                            filteredAndSortedPunches.filter(
                              (p) => p?.approved === null
                            ).length
                        }
                        checked={
                          filteredAndSortedPunches.filter(
                            (p) => p?.approved === null
                          ).length > 0 &&
                          selectedPunches.size ===
                            filteredAndSortedPunches.filter(
                              (p) => p?.approved === null
                            ).length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "user"}
                        direction={orderBy === "user" ? order : "asc"}
                        onClick={() => handleRequestSort("user")}
                      >
                        Utente
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "rewardCoins"}
                        direction={orderBy === "rewardCoins" ? order : "asc"}
                        onClick={() => handleRequestSort("rewardCoins")}
                      >
                        Punteggio
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    >
                      Penalità
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "date"}
                        direction={orderBy === "date" ? order : "asc"}
                        onClick={() => handleRequestSort("date")}
                      >
                        Data Timbratura
                      </TableSortLabel>
                    </TableCell>
                    {/*<TableCell>*/}
                    {/*  <TableSortLabel*/}
                    {/*    active={orderBy === "createdAt"}*/}
                    {/*    direction={orderBy === "createdAt" ? order : "asc"}*/}
                    {/*    onClick={() => handleRequestSort("createdAt")}*/}
                    {/*  >*/}
                    {/*    Data Richiesta*/}
                    {/*  </TableSortLabel>*/}
                    {/*</TableCell>*/}
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    >
                      Timbratura Originale
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    >
                      Timbratura Richiesta
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    >
                      Stato
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "0.8rem",
                        color: "secondary.main"
                      }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedPunches.map(
                    (pendingPunch: PendingPunchType) => {
                      const dayUser = pendingPunch?.day?.user;
                      const dayDate = pendingPunch?.day?.date;
                      const isProcessing =
                        processingId === pendingPunch.documentId;
                      const userName = `${dayUser?.firstName || ""} ${
                        dayUser?.lastName || ""
                      }`;

                      return (
                        <TableRow key={pendingPunch.documentId}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedPunches.has(
                                pendingPunch.documentId as string
                              )}
                              onChange={() =>
                                handleSelectPunch(
                                  pendingPunch.documentId as string
                                )
                              }
                              disabled={pendingPunch?.approved !== null}
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                component="span"
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  bgcolor: dayUser?.color || "#ccc",
                                  flexShrink: 0
                                }}
                              />
                              <Typography variant="body2">
                                {userName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.2}>
                              {dayUser?.rewardCoins !== null &&
                              dayUser?.rewardCoins !== undefined ? (
                                <>
                                  <StarsIcon
                                    sx={{
                                      color:
                                        dayUser.rewardCoins <= 0
                                          ? "#999"
                                          : "#FFD700",
                                      fontSize: 18
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                      color:
                                        dayUser.rewardCoins <= 0
                                          ? "error.main"
                                          : "#FFD700"
                                    }}
                                  >
                                    {dayUser.rewardCoins}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2">-</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.2}>
                              {pendingPunch?.penalty !== null &&
                              pendingPunch?.penalty !== undefined ? (
                                <>
                                  <StarsIcon
                                    sx={{
                                      color: "warning.main",
                                      fontSize: 18
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                      color: "warning.main"
                                    }}
                                  >
                                    -{pendingPunch.penalty}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2">-</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {dayDate
                              ? moment(dayDate).format("DD/MM/YYYY")
                              : "-"}
                          </TableCell>
                          {/*<TableCell>*/}
                          {/*  {pendingPunch?.createdAt*/}
                          {/*    ? moment(pendingPunch.createdAt).format(*/}
                          {/*        "DD/MM/YYYY HH:mm"*/}
                          {/*      )*/}
                          {/*    : "-"}*/}
                          {/*</TableCell>*/}
                          <TableCell>
                            <Box>
                              {pendingPunch?.in && (
                                <Typography variant="body2">
                                  Ingresso:{" "}
                                  {pendingPunch?.oldIn
                                    ? moment(
                                        pendingPunch.oldIn,
                                        "HH:mm:ss"
                                      ).format("HH:mm")
                                    : "-"}
                                </Typography>
                              )}
                              {pendingPunch?.out && (
                                <Typography variant="body2">
                                  Uscita:{" "}
                                  {pendingPunch?.oldOut
                                    ? moment(
                                        pendingPunch.oldOut,
                                        "HH:mm:ss"
                                      ).format("HH:mm")
                                    : "-"}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              {pendingPunch?.in && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700
                                  }}
                                >
                                  Ingresso:{" "}
                                  {moment(pendingPunch.in, "HH:mm:ss").format(
                                    "HH:mm"
                                  )}
                                </Typography>
                              )}
                              {pendingPunch?.out && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700
                                  }}
                                >
                                  Uscita:{" "}
                                  {moment(pendingPunch.out, "HH:mm:ss").format(
                                    "HH:mm"
                                  )}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Box>
                              {pendingPunch?.approved === true && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "success.main",
                                    fontWeight: 600
                                  }}
                                >
                                  Approvata
                                </Typography>
                              )}
                              {pendingPunch?.approved === false && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "error.main",
                                    fontWeight: 600
                                  }}
                                >
                                  Rifiutata
                                </Typography>
                              )}
                              {pendingPunch?.approved === null && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "warning.main",
                                    fontWeight: 600
                                  }}
                                >
                                  In attesa
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={1} justifyContent="center">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={
                                  isProcessing ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : (
                                    <CheckCircle />
                                  )
                                }
                                onClick={() =>
                                  handleApprove(
                                    pendingPunch.documentId as string
                                  )
                                }
                                disabled={
                                  isProcessing ||
                                  pendingPunch?.approved !== null ||
                                  (dayUser?.rewardCoins !== undefined &&
                                    dayUser?.rewardCoins !== null &&
                                    dayUser.rewardCoins <= 0)
                                }
                                title={
                                  dayUser?.rewardCoins !== undefined &&
                                  dayUser?.rewardCoins !== null &&
                                  dayUser.rewardCoins <= 0
                                    ? "L'utente non ha punti sufficienti"
                                    : ""
                                }
                              >
                                Approva
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={
                                  isProcessing ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : (
                                    <Cancel />
                                  )
                                }
                                onClick={() =>
                                  handleReject(
                                    pendingPunch.documentId as string
                                  )
                                }
                                disabled={
                                  isProcessing ||
                                  pendingPunch?.approved !== null
                                }
                              >
                                Rifiuta
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ))}

      {/* Dialog di conferma */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {confirmDialog.type === "approve"
            ? "Conferma Approvazione"
            : "Conferma Rifiuto"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.type === "approve"
              ? `Sei sicuro di voler approvare ${selectedPunches.size} ${
                  selectedPunches.size === 1 ? "richiesta" : "richieste"
                }?`
              : `Sei sicuro di voler rifiutare ${selectedPunches.size} ${
                  selectedPunches.size === 1 ? "richiesta" : "richieste"
                }?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="inherit">
            Annulla
          </Button>
          <Button
            onClick={handleConfirm}
            color={confirmDialog.type === "approve" ? "success" : "error"}
            variant="contained"
            autoFocus
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApprovazionePage;
